import { FC, Fragment } from 'react'
import { Menu, TopBar } from '@reapit/elements'
import { SecondaryNavigationMenu } from '../types'
import { SubMenu } from './SubMenu'
import { SecondaryMenuItem } from './SecondaryMenuItem'

interface SecondaryMenuProps {
  menu: SecondaryNavigationMenu[]
}

/**
 * `SecondaryMenu` is a dropdown menu component used in the top navigation bar.
 * It displays a list of secondary navigation items, each with optional submenus.
 *
 * Props:
 * @param menu (SecondaryNavigationMenu[]): List of secondary menu items, where each item may include:
 * - `label` (string): The label displayed for the menu item.
 * - `icon` (IconNames): The icon associated with the menu item.
 * - `hasBadge` (boolean): Optional flag to display a badge on the menu item.
 * - `subMenu` (array, optional): A list of submenu items, each with:
 *    - `label` (string): Text displayed for the submenu item.
 *    - `onClick` (function): Action triggered when the submenu item is selected.
 *
 * This component:
 * 1. If a menu item includes a `subMenu`, it is rendered as a menu (`TopBar.SecondaryNav.MenuItem`)
 *   with `Menu.Divider`s between sub-items.
 * 2. Otherwise, it's rendered as a direct navigation item (`TopBar.SecondaryNav.Item`)
 * with optional badge and active state.
 * 3. Menu items and submenus are conditionally shown based on `hasAccess !== false`.
 */
export const SecondaryMenu: FC<SecondaryMenuProps> = ({ menu }) => {
  return (
    <TopBar.SecondaryNav>
      {menu
        .filter((secondaryMenuItem) => secondaryMenuItem.hasAccess !== false)
        .map((secondaryMenuItem, i) => {
          const { subMenu, label, icon } = secondaryMenuItem
          if (subMenu?.length) {
            return (
              <TopBar.SecondaryNav.MenuItem key={i} aria-label={label as string} icon={icon}>
                {subMenu
                  ?.filter((subMenuItem) => subMenuItem.hasAccess !== false)
                  .map((subMenuItem, index, arr) => (
                    <Fragment key={subMenuItem.label}>
                      <SubMenu {...subMenuItem} key={subMenuItem.label} />
                      {index < arr.length - 1 && subMenuItem.displayAsGroup !== false && <Menu.Divider />}
                    </Fragment>
                  ))}
              </TopBar.SecondaryNav.MenuItem>
            )
          }
          return <SecondaryMenuItem key={i} {...secondaryMenuItem} />
        })}
    </TopBar.SecondaryNav>
  )
}
